import React from 'react';
import { t } from '../../../utils/textUtils';
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormHelperText,
  Paper,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import id from 'date-fns/locale/id';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const ScheduleForm = ({ formData, onChange, errors = {} }) => {
  const handleDateChange = (field, date) => {
    // Create an update object with the changed date
    const update = { [field]: date };
    
    // If both dates are available after this change, calculate and include duration
    const startDate = field === 'startDate' ? date : formData.startDate;
    const endDate = field === 'estimatedEndDate' ? date : formData.estimatedEndDate;
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      update.durasiProyek = diffDays;
    }
    
    onChange(update);
  };

  // Calculate duration in days for display
  const calculateDuration = () => {
    if (formData.startDate && formData.estimatedEndDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.estimatedEndDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return null;
  };

  const duration = calculateDuration();
  
  // Ensure durasiProyek is updated in formData if it's not already set
  React.useEffect(() => {
    if (duration && formData.durasiProyek !== duration) {
      onChange({ durasiProyek: duration });
    }
  }, [duration, formData.durasiProyek, onChange]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
      <Box>
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 2,
            borderColor: 'divider',
            bgcolor: '#F8FAFC'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: '#ffffff',
                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CalendarTodayIcon sx={{ color: '#4F46E5' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {t('WAKTU DAN JADWAL')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('Tentukan jadwal pelaksanaan proyek konstruksi prefabrikasi.')}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            borderRadius: 2, 
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.5 }}>
              {t('JADWAL PROYEK')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Tentukan jadwal pelaksanaan proyek konstruksi prefabrikasi.')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label={t('TANGGAL MULAI PROYEK *')}
                value={formData.startDate}
                onChange={(date) => handleDateChange('startDate', date)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    error={!!errors.startDate}
                    helperText={errors.startDate || t('Bisa memilih tanggal di masa lalu')}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label={t('TANGGAL TARGET SELESAI *')}
                value={formData.estimatedEndDate}
                onChange={(date) => handleDateChange('estimatedEndDate', date)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    error={!!errors.estimatedEndDate}
                    helperText={errors.estimatedEndDate || t('Harus setelah tanggal mulai proyek')}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
                minDate={formData.startDate ? new Date(formData.startDate) : null}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('DURASI PROYEK (HARI)')}
                value={duration || ''}
                InputProps={{
                  readOnly: true,
                }}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#3B82F6'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6'
                    }
                  }
                }}
              />
              <FormHelperText>
                {t('Dihitung otomatis dari tanggal mulai dan target selesai')}
              </FormHelperText>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(30, 64, 175, 0.05))',
              border: '1px dashed rgba(59, 130, 246, 0.3)'
            }}
          >
            <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
              {t('Tips: Gunakan tanggal mulai aktual jika proyek sudah berjalan agar laporan progres akurat.')}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ScheduleForm;
